import i18next from "i18next";

import moment from "moment";

import { changeLanguageHeader } from "config/axios";
import store from 'config/redux/index';
import { reset } from "config/redux/slice";
import { AppConstants, ServiceConstants } from "constant";
import { CardResponse, CreditRequestChildResponse, CreditRequestEmploymentResponse, CreditRequestForm, CreditRequestIncomeResponse, CreditResponse, TabItemProp } from "types";
import { StorageUtils } from "utils";

const {
  STORAGE_ACCESS_TOKEN,
  STORAGE_VALID_TO,
  STORAGE_USER,
  CREDIT_CONSTANT_C_HIGHER,
  CREDIT_CONSTANT_C_LOWER,
  NATIONALITY_PRIORITY_LIST
} = AppConstants;

const {
  REQUEST_ERROR,
  REQUEST_PENDING,
  REQUEST_SUCCESS
} = ServiceConstants;


export function isEmptySimple(object: any) {
  return (object === undefined || object === null);
}

export function isEmpty(object: any) {
  if (object === undefined || object === null) {
    return true;
  }
  if (typeof object === 'number') {
    object = new Number(object);
  } else if (typeof object === 'string') {
    object = new String(object);
  }
  if (object instanceof Number) {
    return false;
  }
  if (object instanceof String) {
    return object.trim().length === 0;
  }
  if (object instanceof Array) {
    return object.length === 0;
  }
  if (object instanceof Date) {
    return !object;
  }
  return Object.keys(object).length === 0;
}

export function convertToAppropriateValue(value: any) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  if (value instanceof Array) {
    const newArray: any = new Array(value.length);
    value.forEach((el, index) => {
      newArray[index] = convertToAppropriateValue(el);
    })
    return newArray;
  }
  if (value instanceof Object) {
    const newObject: any = {};
    Object.keys(value).forEach(key => {
      newObject[key] = convertToAppropriateValue(value[key]);
    });
    return newObject;
  }
  return value;
}

export function isEmailValid(email?: string): boolean {
  if (!email) {
    return false;
  }
  const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return pattern.test(email);
}

export function isCountryCodeValid(code: string) {
  const pattern = /^[0-9]*$/;
  return pattern.test(code);
}

export function isPhoneValid(phone: string) {
  const pattern = /^[0-9]*$/;
  return pattern.test(phone);
}

export function capitalize(text: string | undefined) {
  if (!text) {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function updateTabNumber(tabs: TabItemProp[] | undefined, id: string, number: number) {
  if (!tabs) {
    return undefined;
  }
  tabs.forEach(el => {
    if (el.id === id) el.number = number;
  })
  return tabs;
}

export function logout() {
  StorageUtils.deleteKey(STORAGE_ACCESS_TOKEN);
  StorageUtils.deleteKey(STORAGE_VALID_TO);
  StorageUtils.deleteKey(STORAGE_USER);
}

export function changeLanguage(lang: string) {
  console.log('lang', lang);
  changeLanguageHeader(lang.toLowerCase());
  i18next.changeLanguage(lang.toLowerCase());
}

export function resolveServiceObject(serviceObject: any, serviceId: string, onSuccess: (val: any) => void, onError?: (val: any) => void) {
  if (isEmpty(serviceObject)) {
    return;
  }
  if (serviceObject.status === REQUEST_PENDING) {
    return;
  }
  store.dispatch(reset(serviceId))
  const object = serviceObject;
  if (object.status === REQUEST_SUCCESS) {
    onSuccess(object.data);
  } else if (object.status === REQUEST_ERROR) {
    onError?.(object);
  }
}

export function composeTabTitle(name?: string) {
  if (name) {
    return name;
  }
  return 'Webkredit.ch';
}

export function composeAddress(el: CardResponse | CreditResponse) {
  let composite = '';
  if (el.street) composite += el.street;
  if (el.houseNumber) composite += composite.length > 0 ? (' ' + el.houseNumber) : el.houseNumber;
  if (el.city) composite += composite.length > 0 ? (', ' + el.city) : el.city;
  if (el.postal) composite += composite.length > 0 ? (', ' + el.postal) : el.postal;
  return composite;
}

export function removeObject(form: any, index: number, object: any) {
  const transformed: any = form.getTransformedValues();
  const tempArray: any[] = Object.keys(transformed)
    .map(key => transformed[key])
    .filter((_el, i) => i !== index);
  if (tempArray.length === 0 && object) {
    tempArray.push(object);
  }
  form.setInitialValues(tempArray);
  form.reset();
}

export function addNewObject(form: any, object: any) {
  const tempArray = form.getTransformedValues();
  tempArray[Object.keys(tempArray).length] = object;
  form.setInitialValues(tempArray);
  form.reset();
}

export const getCreditLimit = (creditAmount: number | undefined, paymentPeriod: number | undefined, interestRate: number): number => {
  if (!creditAmount || !paymentPeriod) {
    return 0;
  }
  const c = Math.pow(interestRate + 1, 1 / 12);
  const result = creditAmount * Math.pow(c, paymentPeriod) / (Math.pow(c, paymentPeriod!) - 1) * (c - 1);
  return Math.round(result);
}

export const populateMonthlyInstallment = (creditAmount: number, paymentPeriod: number, form: any) => {
  const minLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_LOWER);
  const maxLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_HIGHER);

  form.setFieldValue('monthlyInstallmentFrom', minLimit);
  form.setFieldValue('monthlyInstallmentTo', maxLimit);
}

export function getOrderedCountryList(nationalities: string[] | undefined, t: any) {
  if (!nationalities) {
    return [];
  }
  const top: string[] = [];
  const others: string[] = [];

  for (const item of nationalities) {
    if (item !== 'OTHER') {
      if (NATIONALITY_PRIORITY_LIST.includes(item)) {
        top.push(item);
      } else {
        others.push(item);
      }
    }
  }

  const topRenderedArray = top.map(el => ({ value: el, label: t(`user.nationality.options.${el}`) }));
  const otherRenderedArray = others.map(el => ({ value: el, label: t(`user.nationality.options.${el}`) }));
  otherRenderedArray.sort((a, b) => a.label.localeCompare(b.label));
  otherRenderedArray.push({ value: 'OTHER', label: t(`user.nationality.options.OTHER`) })

  return [
    ...topRenderedArray,
    ...otherRenderedArray
  ];
}

export function isYoungerThanTwelveMonths(date: Date): boolean {
  const considerationDate = new Date();
  considerationDate.setMonth(considerationDate.getMonth() - 12);
  return date > considerationDate;
}

export function clearArrayFieldsWhenUpdating(values: any, previous: any, newVisibleFields: string[]) {
  newVisibleFields.forEach(el => {
    if (!newVisibleFields.includes(el) && previous[el] !== undefined) {
      const [, index, fieldName] = el.split('.');
      values[index][fieldName] = undefined;
    }
  });
}

export function updateArrayFieldsWhenVisibilityChanges(prevVisibleFields: string[], newVisibleFields: string[], values: any, form: any) {
  if (prevVisibleFields.length > 0) {
    prevVisibleFields.forEach(el => {
      if (!newVisibleFields.includes(el)) {
        const [, index, fieldName] = el.split('.')
        if (values[index] && values[index][fieldName] !== undefined) {
          values[index][fieldName] = undefined;
          form.setFieldValue(`${index}.${fieldName}`, undefined);
        }
      }
    })
  }
}

export const getVisibleRequestAndPersonalFields = (values: CreditRequestForm) => {
  const array = [];
  if (values.creditType) {
    array.push('creditPurpose');
    if (values.creditType === 'REFINANCING') {
      array.push('existingMonthlyInstallment');
      array.push('bank');
      if (values.bank === 'OTHER') {
        array.push('bankName');
      }
      array.push('refinanceCreditCard');
      array.push('refinanceLease');
    }
  }
  if (values.creditPurpose === 'OTHER') {
    array.push('creditPurposeNote');
  }
  if (values.saturdayAvailability) {
    array.push('saturdayAvailabilityFrom');
    array.push('saturdayAvailabilityTo');
  }
  if (values.nationality && values.nationality !== 'SWITZERLAND' && values.nationality !== 'LIECHTENSTEIN') {
    array.push('visaType');
    if (values.visaType === 'B') {
      array.push('visaFrom');
    }
  }
  if (values.livingFrom) {
    const considerationDate = new Date();
    considerationDate.setMonth(considerationDate.getMonth() - 12);
    if (new Date(values.livingFrom) > considerationDate) {
      array.push('previousPostal');
      array.push('previousCity');
      array.push('previousStreet');
      array.push('previousHouseNumber');
    }
  }
  if (values.maritalStatus === 'MARRIED') {
    array.push('partnerOnSameAddress');
    if (values.partnerOnSameAddress) {
      array.push('partnerIncludeInApplication');
      if (values.partnerIncludeInApplication) {
        array.push('partnerFirstName');
        array.push('partnerLastName');
        array.push('partnerGender');
        array.push('partnerBirthDate');
        array.push('partnerEmail');
        array.push('partnerPhoneNumber');
        array.push('partnerBirthDate');
        array.push('partnerNationality');
        array.push('partnerWorkdayAvailabilityFrom');
        array.push('partnerWorkdayAvailabilityTo');
        array.push('partnerSaturdayAvailability');
        if (values.partnerSaturdayAvailability) {
          array.push('partnerSaturdayAvailabilityFrom');
          array.push('partnerSaturdayAvailabilityTo');
        }
        if (values.partnerNationality && values.partnerNationality !== 'SWITZERLAND' && values.partnerNationality !== 'LIECHTENSTEIN') {
          array.push('partnerVisaType');
          if (values.partnerVisaType === 'B') {
            array.push('partnerVisaFrom');
          }
        }
      }
    }
  }

  return array;
}

export function getVisibleChildrenFields(values: any) {
  const tempArray: string[] = [];
  if (values) {
    let array: CreditRequestChildResponse[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    Object.keys(values).forEach(key => {
      const item: CreditRequestChildResponse = values[key as any];
      if (item.craft) {
        tempArray.push(`children.${key}.craftAmount`)
      }
    })
  }
  return tempArray
}

export function getVisibleEmploymentFields(values: any) {
  const tempArray: string[] = [];
  if (values) {
    let array: CreditRequestEmploymentResponse[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    array.forEach((el, index) => {
      if (el.professionalSituation) {
        if (el.professionalSituation === 'EMPLOYED' || el.professionalSituation === 'AGENCY') {
          if (el.professionalSituation === 'EMPLOYED') {
            tempArray.push(`employments.${index}.thirteenthSalary`);
            tempArray.push(`employments.${index}.bonusReceived`);
          }
          if (el.professionalSituation === 'AGENCY') {
            tempArray.push(`employments.${index}.employmentType`);
          }
          tempArray.push(`employments.${index}.contractStartDate`);
          if (el.employmentType === 'DEFINED') {
            tempArray.push(`employments.${index}.contractEndDate`);
          }
          tempArray.push(`employments.${index}.travelingToWork`);
        } else if (el.professionalSituation === 'SELF_EMPLOYED_AG') {
          tempArray.push(`employments.${index}.requesterPaysSalary`);
        } else if (el.professionalSituation === 'PENSION') {
          tempArray.push(`employments.${index}.socialHelp`);
        }
      }
    })
  }
  return tempArray;
}

export function getVisibleIncomeFields(values: any) {
  const tempArray: string[] = [];
  if (values) {
    let array: CreditRequestIncomeResponse[] = [];
    if (!(values instanceof Array)) {
      Object.keys(values).forEach(key => array.push(values[key]));
    } else {
      array = values;
    }
    Object.keys(values).forEach(key => {
      const item: CreditRequestIncomeResponse = values[key as any];
      if (item.incomeType === 'RENT') {
        tempArray.push(`incomes.${key}.startDate`)
      }
    })
  }
  return tempArray
}